<script setup lang="ts">
import { useDebounceFn, useEventListener } from "@vueuse/core";
import { ref, watch } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { projectLibrary } from "../../data";
import { t } from "../../i18n";
import { appStore } from "../../stores/app";
import { FormatHelper } from "../../utils/format";
import { goToPage, loadProjects, projectList, statusName } from "./projectList";

const filter = projectList.filter;
const openIdentifier = ref("");

// Every time the page is shown: clear the "open configuration" field and reload the list
watch(() => appStore.pageVisits, () => {
    if (appStore.page !== "configurations") return;
    openIdentifier.value = "";
    loadProjects();
});

// Search (debounced while typing) and "hide inactive"
const onSearchInput = useDebounceFn((value: string) => {
    filter.search = value;
    loadProjects();
}, 250);

function clearSearch() {
    filter.search = "";
    loadProjects();
}

function onHideInactiveChanged(checked: boolean) {
    filter.hideInactive = checked;
    loadProjects();
}

// Row action menu: one open at a time, any click elsewhere closes it
const openActionsFor = ref<string | null>(null);
useEventListener(document, "click", () => { openActionsFor.value = null; });

async function deleteProject(identifier: string) {
    if (!identifier) return;
    if (window.confirm(t("projectlist.deleteconfirm"))) {
        await projectLibrary.deleteProject(identifier);
        loadProjects();
    }
}

function editProject(identifier: string) {
    if (!identifier) return;
    appStore.editProjectId = identifier;
    appStore.modal = "editProject";
}

function openConfiguration(identifier: string) {
    return getLegacyApp().openConfiguration(identifier);
}

function newProject() {
    appStore.modal = "createProject";
}
</script>

<!-- Shown when appStore.page is "configurations" (set by showPage in app.ts) -->
<template>
    <div class="configurations-page page bulma-contextual" data-page="configurations" :class="{ 'is-shown': appStore.page === 'configurations' }">
        <div class="page__content columns is-gapless is-centered">
            <div class="column is-12-mobile is-10-tablet">
                <div class="mb-5">
                    <h3 class="title is-3" v-html="t('page.configuration.savedconfigurations')"></h3>
                </div>
                <div class="mb-5">
                    <div class="is-flex is-justify-content-space-between is-align-items-center">
                        <div class="is-flex is-align-items-center">
                            <div class="projectsearch control has-icons-left is-flex-shrink-3 mr-5">
                                <form @submit.prevent @reset="clearSearch">
                                    <input id="projectsearch" name="projectsearch" required type="text" class="simple-input input is-shadowless is-radiusless" :placeholder="t('common.search')" @keyup="onSearchInput(($event.target as HTMLInputElement).value)" />
                                    <span class="icon is-small is-left">
                                        <i class="fas fa-search"></i>
                                    </span>
                                    <button class="icon close-icon is-right" type="reset"></button>
                                </form>
                            </div>
                            <div class="is-flex-shrink-3">
                                <label class="checkbox">
                                    <input id="hideinactive-projects" type="checkbox" :checked="filter.hideInactive" @change="onHideInactiveChanged(($event.target as HTMLInputElement).checked)">
                                    <span v-html="t('page.configuration.hideinactive')"></span>
                                </label>
                            </div>
                        </div>
                        <div class="pagenation is-flex is-align-items-center">
                            <div class="pagenation-pagecount mr-4" v-html="projectList.pageCount > 0 ? t('projectlist.currentpages', { count: filter.currentPage, pages: projectList.pageCount }) : ''">
                            </div>
                            <div>
                                <button class="button first" :disabled="filter.currentPage <= 1" @click="goToPage(1)">
                                    <span class="icon is-small">
                                        <i class="fas fa-angle-double-left"></i>
                                    </span>
                                </button>
                                <button class="button prev" :disabled="filter.currentPage <= 1" @click="goToPage(filter.currentPage - 1)">
                                    <span class="icon is-small">
                                        <i class="fas fa-angle-left"></i>
                                    </span>
                                </button>
                                <button class="button next" :disabled="filter.currentPage >= projectList.pageCount" @click="goToPage(filter.currentPage + 1)">
                                    <span class="icon is-small">
                                        <i class="fas fa-angle-right"></i>
                                    </span>
                                </button>
                                <button class="button last" :disabled="filter.currentPage >= projectList.pageCount" @click="goToPage(projectList.pageCount)">
                                    <span class="icon is-small is-left">
                                        <i class="fas fa-angle-double-right"></i>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mb-5">
                    <table class="project-list table is-fullwidth is-striped is-hoverable">
                        <thead>
                            <tr>
                                <th v-html="t('projectlist.projectname')"></th>
                                <th v-html="t('common.email')"></th>
                                <th v-html="t('common.phonenumber')"></th>
                                <th v-html="t('projectlist.projectdate')"></th>
                                <th v-html="t('common.status')"></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="project in projectList.projects" :key="project.identifier">
                                <td>{{ project.name }}</td>
                                <td>{{ project.seller }}</td>
                                <td>{{ project.phone }}</td>
                                <td>{{ FormatHelper.formatDate(project.added) }}</td>
                                <td><span class="status mr-2"><i :class="'status-' + statusName(project.status)"></i></span><span v-html="t('common.statuses.' + statusName(project.status))"></span></td>
                                <td>
                                    <div class="project-actions dropdown is-right" :class="{ 'is-active': openActionsFor === project.identifier }">
                                        <div class="dropdown-trigger" @click.prevent.stop="openActionsFor = project.identifier">
                                            <a><span class="icon is-small"><i class="fas fa-ellipsis-h"></i></span></a>
                                        </div>
                                        <div class="dropdown-menu">
                                            <div class="dropdown-content">
                                                <a href="#" class="open-project dropdown-item" @click="openConfiguration(project.identifier)">
                                                    <span v-html="t('projectlist.open')"></span>
                                                    <span class="icon is-small is-right"><i class="fas fa-long-arrow-right"></i></span>
                                                </a>
                                                <a href="#" class="edit-project dropdown-item" @click="editProject(project.identifier)">
                                                    <span v-html="t('projectlist.edit')"></span>
                                                    <span class="icon is-small is-right"><i class="fas fa-cog"></i></span>
                                                </a>
                                                <a href="#" class="delete-project dropdown-item" @click="deleteProject(project.identifier)">
                                                    <span v-html="t('projectlist.delete')"></span>
                                                    <span class="icon is-small is-right"><i class="fas fa-trash"></i></span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="is-flex is-justify-content-flex-end">
                    <div class="field is-grouped is-horizontal mb-6">
                        <div class="control">
                            <form id="openprojectform" @submit.prevent="openConfiguration(openIdentifier)">
                                <div class="field has-addons simple-input is-fullheight">
                                    <div class="control mr-4">
                                        <input v-model="openIdentifier" type="text" required class="input is-radiusless is-shadowless" :placeholder="t('page.configuration.typeconfigid')" />
                                    </div>
                                    <div class="control">
                                        <button id="openproject-button" type="submit" class="button is-default is-black is-bordered is-radiusless">
                                            <span v-html="t('page.configuration.openconfig')"></span>
                                            <span class="icon is-small">
                                                <i class="fas fa-long-arrow-alt-right"></i>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div class="control">
                            <button id="newproject-button" class="button is-default is-white is-bordered is-radiusless" @click="newProject">
                                <span v-html="t('page.configuration.newconfig')"></span>
                                <span class="icon is-small">
                                    <i class="fas fa-long-arrow-alt-right"></i>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
